import React, { createContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { apiClient } from '../api';

interface AuthContextType {
  isAuthenticated: boolean;
  userEmail: string | null;
  token: string | null;
  login: (token: string, email: string) => void;
  logout: () => void;
  isLoading: boolean;
  refreshToken: () => Promise<void>;
  /** True when the token expired and silent refresh failed — UI should show a re-login prompt */
  needsReauth: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_STORAGE_KEY = 'auth_token';
const EMAIL_STORAGE_KEY = 'user_email';
const TOKEN_EXPIRY_KEY = 'token_expiry';

// Check token expiry every 2 minutes
const TOKEN_CHECK_INTERVAL = 2 * 60 * 1000;

interface AuthProviderProps {
  children: ReactNode;
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

const AuthProviderInner: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsReauth, setNeedsReauth] = useState(false);
  const refreshInProgress = useRef(false);

  const logout = useCallback(() => {
    setToken(null);
    setUserEmail(null);
    setNeedsReauth(false);
    apiClient.clearToken();
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(EMAIL_STORAGE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }, []);

  const persistToken = useCallback((newToken: string, email: string, expiresInSeconds = 3600) => {
    const expiryTime = Date.now() + expiresInSeconds * 1000;
    setToken(newToken);
    setUserEmail(email);
    setNeedsReauth(false);
    apiClient.setToken(newToken);
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    localStorage.setItem(EMAIL_STORAGE_KEY, email);
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
  }, []);

  const login = useCallback((newToken: string, email: string) => {
    persistToken(newToken, email, 3600);
  }, [persistToken]);

  /**
   * Silently request a fresh access token from Google using the token client.
   * Works without a popup as long as the user has an active Google session.
   * Falls back to setting needsReauth=true (shows a re-login banner) instead
   * of logging the user out and discarding unsaved work.
   */
  const refreshToken = useCallback(async (): Promise<void> => {
    if (refreshInProgress.current) return;
    refreshInProgress.current = true;

    const email = localStorage.getItem(EMAIL_STORAGE_KEY);
    if (!email || !GOOGLE_CLIENT_ID) {
      refreshInProgress.current = false;
      logout();
      return;
    }

    try {
      await new Promise<void>((resolve, reject) => {
        // Google Identity Services token client — silent refresh (no popup)
        const tokenClient = (window as any).google?.accounts?.oauth2?.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'openid email profile',
          hint: email,
          prompt: '', // empty string = silent, no consent screen
          callback: async (response: any) => {
            if (response.error) {
              reject(new Error(response.error));
              return;
            }
            try {
              // Fetch updated user info to confirm email
              const userInfoRes = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                headers: { Authorization: `Bearer ${response.access_token}` },
              });
              const userInfo = userInfoRes.ok ? await userInfoRes.json() : { email };
              persistToken(response.access_token, userInfo.email || email, response.expires_in || 3600);
              resolve();
            } catch {
              persistToken(response.access_token, email, response.expires_in || 3600);
              resolve();
            }
          },
          error_callback: (err: any) => {
            reject(new Error(err?.type || 'silent_refresh_failed'));
          },
        });

        if (!tokenClient) {
          reject(new Error('Google Identity Services not loaded'));
          return;
        }

        tokenClient.requestAccessToken();
      });
    } catch (err) {
      console.warn('Silent token refresh failed, user needs to re-authenticate:', err);
      // Don't log out — just flag that re-auth is needed so the UI can prompt
      // the user without destroying their unsaved work.
      setNeedsReauth(true);
    } finally {
      refreshInProgress.current = false;
    }
  }, [logout, persistToken]);

  // Load token from localStorage on mount — restore session across visits
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    const storedEmail = localStorage.getItem(EMAIL_STORAGE_KEY);
    const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (storedToken && storedEmail) {
      const isExpired = tokenExpiry && Date.now() >= parseInt(tokenExpiry, 10);

      if (!isExpired) {
        // Token still valid — restore session
        setToken(storedToken);
        setUserEmail(storedEmail);
        apiClient.setToken(storedToken);
      } else {
        // Token expired but we still have the email — keep the user "logged in"
        // and immediately try a silent refresh. This avoids losing unsaved state
        // when the user returns after a while.
        setToken(storedToken);
        setUserEmail(storedEmail);
        apiClient.setToken(storedToken);
        // Kick off a silent refresh after the component mounts
        setTimeout(() => refreshToken(), 500);
      }
    }

    setIsLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for 401 responses — try silent refresh instead of immediate logout
  useEffect(() => {
    const handleUnauthorized = () => {
      refreshToken();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [refreshToken]);

  // Periodically check token expiry and refresh proactively
  useEffect(() => {
    if (!token) return;

    const checkInterval = setInterval(() => {
      const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
      if (tokenExpiry) {
        const timeUntilExpiry = parseInt(tokenExpiry, 10) - Date.now();
        // Refresh when less than 5 minutes remain
        if (timeUntilExpiry < 5 * 60 * 1000) {
          console.log('Token expiring soon, refreshing silently…');
          refreshToken();
        }
      }
    }, TOKEN_CHECK_INTERVAL);

    return () => clearInterval(checkInterval);
  }, [token, refreshToken]);

  const value: AuthContextType = {
    isAuthenticated: !!token,
    userEmail,
    token,
    login,
    logout,
    isLoading,
    refreshToken,
    needsReauth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Wrapper component that provides Google OAuth context
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  if (!GOOGLE_CLIENT_ID) {
    console.error('VITE_GOOGLE_CLIENT_ID environment variable is not set');
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </GoogleOAuthProvider>
  );
};

export { AuthContext };
export type { AuthContextType };
